import {Grid, Stack} from '@mui/material';
import {AppButton} from 'components/buttons';
import {AppDialog} from 'components/dialogs';
import {AppControlledCheckbox, AppControlledDatePicker, AppControlledInput, AppInput} from 'components/form';
import {AppControlledEditor} from 'components/form/AppTextEditor';
import {useCreateSprint, useUpdateSprint} from 'hooks';
import {SprintResponseModel, SprintCreateUpdateRequestModel, sprintCreateDefaultValues} from 'models/sprint.model';
import React, {useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {useParams} from 'react-router-dom';

export interface AddSprintDialogProps {
    open: boolean;
    onCloseDialog: (id?: string) => void;
    sprintToUpdate?: SprintResponseModel;
    handleAddSprint: (value: SprintResponseModel) => void;
    handleUpdateSprint: (value: SprintResponseModel) => void;
}

export const AddUpdateSprintDialog: React.FC<AddSprintDialogProps> = ({
    open,
    onCloseDialog,
    handleAddSprint,
    handleUpdateSprint,
    sprintToUpdate,
}) => {
    const {
        control,
        setValue,
        handleSubmit,
        reset,
        watch,
        formState: {isValid},
    } = useForm<SprintCreateUpdateRequestModel>({
        defaultValues: sprintCreateDefaultValues,
    });
    const values = watch();
    const {boardId} = useParams();
    const {createSprint, createSprintLoading, createSprintResponse} = useCreateSprint();
    const {updateSprint, updateSprintLoading, updateSprintResponse} = useUpdateSprint();
    const subHandler = (values: SprintCreateUpdateRequestModel) => {
        if (boardId) {
            values.boardSlug = boardId;

            if (sprintToUpdate && sprintToUpdate.id) {
                updateSprint({
                    variables: {input: values},
                });
            } else {
                createSprint({
                    variables: {input: values},
                });
            }
        }
    };

    useEffect(() => {
        if (sprintToUpdate && sprintToUpdate.id) {
            setValue('id', sprintToUpdate.id);
            setValue('title', sprintToUpdate.title);
            setValue('startData', sprintToUpdate.startData);
            setValue('endData', sprintToUpdate.endData);
            setValue('isPinned', sprintToUpdate.isPinned);
            setValue('details', sprintToUpdate.details);
        }
    }, []);

    useEffect(() => {
        if (!updateSprintLoading && updateSprintResponse) {
            handleUpdateSprint(updateSprintResponse);
            reset();
            onCloseDialog(updateSprintResponse.id);
        }
    }, [updateSprintLoading, updateSprintResponse]);

    useEffect(() => {
        if (!createSprintLoading && createSprintResponse) {
            handleAddSprint(createSprintResponse);
            reset();
            onCloseDialog(updateSprintResponse?.id);
        }
    }, [createSprintLoading, createSprintResponse]);

    return (
        <AppDialog
            title={`${sprintToUpdate ? 'Update' : 'Add'} Sprint`}
            open={open}
            onCloseDialog={() => {
                reset();
                onCloseDialog();
            }}
            children={
                <form onSubmit={handleSubmit(subHandler)}>
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <AppControlledInput required fullWidth control={control} name="title" label={'Title'} />
                        </Grid>
                        <Grid item xs={6}>
                            <AppControlledDatePicker
                                maxDate={new Date(values.endData)}
                                control={control}
                                required={'Start Date is required'}
                                label={'Start Date'}
                                name="startData"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <AppControlledDatePicker
                                minDate={new Date(values.startData)}
                                control={control}
                                required={'End Date is required'}
                                label={'End Date'}
                                name="endData"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <AppControlledCheckbox control={control} label={'Is Pinned?'} name="isPinned" />
                        </Grid>
                        <Grid item xs={12}>
                            <AppControlledEditor control={control} label={'Details'} name="details" />
                        </Grid>
                        <Grid item xs={12}>
                            <Stack direction="row" spacing={2} justifyContent="flex-end">
                                <AppButton
                                    color="error"
                                    variant="outlined"
                                    onClick={() => {
                                        reset();
                                        onCloseDialog();
                                    }}
                                >
                                    Cancel
                                </AppButton>
                                <AppButton
                                    isLoading={createSprintLoading}
                                    isAnimated={isValid}
                                    type="submit"
                                    variant="contained"
                                >
                                    Save
                                </AppButton>
                            </Stack>
                        </Grid>
                    </Grid>
                </form>
            }
        />
    );
};

export default AddUpdateSprintDialog;
